import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { saveOrderLocally, clearLocalOrder, addToSyncQueue } from '../lib/offlineStore';
import { initAuditContext, logAudit, diffObjects } from '../lib/auditLog';
import { useOfflineSync } from '../hooks/useOfflineSync';

export interface CustomerHistory {
  id?: number;
  date: string;
  type: string;
  note: string;
}

export interface Customer {
  id: string;
  name: string;
  contact: string;
  phone: string;
  email: string;
  address: string; // Legacy field
  street?: string;
  zip?: string;
  city?: string;
  lat?: number;
  lng?: number;
  debitorNumber?: string;
  history: CustomerHistory[];
}

export interface OfferMaterial {
  name: string;
  quantity: number;
  price: number;
}

export interface Offer {
  id: string;
  customerId: string;
  date: string;
  total: number;
  status: 'Warten auf Rückmeldung' | 'Angenommen' | 'Abgelehnt' | 'Archiviert' | 'Kunde hat zugesagt';
  referenceOfferId?: string;
  lat?: number;
  lng?: number;
  serviceAddress?: string;
  materials?: OfferMaterial[];
  hours?: number;
  rate?: number;
  travelCost?: number;
  internalNotes?: string;
  changeHistory?: ChangeLogEntry[];
  token?: string;
  signature?: string;
  isAccepted?: boolean;
}

export interface ServicePackage {
  id: string;
  name: string;
  description: string;
  basePrice: number;
  items: OfferMaterial[];
  tenantId: string;
}

export interface ChangeLogEntry {
  date: string;
  user: string;
  changes: string;
}

export interface Supplier {
  id: string;
  name: string;
  contact: string;
  phone: string;
  email: string;
  street: string;
  zip: string;
  city: string;
  creditorNumber?: string;
  notes?: string;
}

export interface InventorySupplier {
  supplierId: string;
  supplierItemNumber?: string;
  purchasePrice: number;
  minOrderQuantity: number;
}

export interface Machine {
  id: string;
  name: string;
  type: string;
  status: 'Verfügbar' | 'Im Einsatz' | 'Wartung' | 'Defekt';
  location: string;
  assignee?: string;
  nextMaintenance: string;
  supplierId?: string;
}

export interface Invoice {
  id: string;
  customerId: string;
  date: string;
  amount: number;
  status: 'Entwurf' | 'Offen' | 'Bezahlt' | 'Storniert' | '1. Mahnung' | '2. Mahnung' | 'Mahnverfahren';
  dueDate: string;
  fromOfferId?: string;
  serviceAddress?: string;
  dunningLevel?: number;
  lastDunningDate?: string;
  dunningFee?: number;
}

export interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
  minStock: number;
  unitPrice: number;
  suppliers?: InventorySupplier[];
}

export interface Vehicle {
  id: string;
  name: string;           // z.B. "VW Crafter Weiß"
  licensePlate: string;   // Kennzeichen
  type: string;           // z.B. "Transporter", "PKW"
  status: 'Verfügbar' | 'Im Einsatz' | 'Wartung' | 'Defekt';
  assignedEmployeeId?: string; // Hauptfahrer
  nextTuev?: string;
  notes?: string;
  tenantId: string;
  supplierId?: string;
}

export interface OrderAssignedEmployee { id: string; name: string; hoursPlanned?: number; }
export interface OrderAssignedVehicle   { id: string; name: string; licensePlate?: string; }
export interface OrderAssignedMachine   { id: string; name: string; }
export interface OrderRequiredMaterial  { name: string; quantity: number; unit: string; unitPrice?: number; }
export interface OrderAdditionalPosition { description: string; quantity: number; unitPrice: number; }

export type LeistungsPositionTyp = 'arbeit' | 'material' | 'anlage' | 'pauschale' | 'sonstiges';
export type LeistungsPositionStatus = 'geplant' | 'in_arbeit' | 'erledigt' | 'entfallen' | 'nachtrag';
export type LeistungsPhase = 'angebot' | 'auftrag' | 'disposition' | 'abnahme' | 'rechnung';

export interface LeistungsPosition {
  id: string;
  tenantId: string;
  orderId?: string;
  offerId?: string;
  positionNr: number;
  parentId?: string;
  typ: LeistungsPositionTyp;
  bezeichnung: string;
  beschreibung?: string;
  menge: number;
  sollMenge?: number;
  istMenge?: number;
  einheit: string;
  einheitspreis: number;
  gesamtpreis: number;
  status: LeistungsPositionStatus;
  bemerkung?: string;
  createdAt?: string;
  createdById?: string;
  createdByName?: string;
}

export interface LeistungsnachweisVersion {
  id: string;
  tenantId: string;
  orderId?: string;
  offerId?: string;
  versionNr: number;
  phase: LeistungsPhase;
  geaendertVonId?: string;
  geaendertVonName?: string;
  geaendertAm: string;
  begruendung: string;
  diffSummary?: string;
  snapshot: LeistungsPosition[];
}

export interface DispoTask {
  id: string;
  type: 'material' | 'machine' | 'vehicle' | 'custom';
  label: string;
  referenceId?: string; // inventory/machine/vehicle ID
  requiredQuantity?: number;
  availableQuantity?: number;
  unit?: string;
  status: 'offen' | 'bestellt' | 'bereit' | 'fehlt';
  checkedBy?: string;
  checkedAt?: string;
  notes?: string;
  // Zeitbereich für Maschinen/Fahrzeuge (Default = gesamte Auftragsdauer)
  startDate?: string;
  endDate?: string;
}

export interface Order {
  id: string;
  offerId?: string;
  customerId: string;
  tenantId: string;
  title: string;
  status: 'Bereit' | 'In Ausführung' | 'Abgeschlossen' | 'Storniert';
  scheduledStart: string;
  scheduledEnd: string;
  serviceAddress?: string;
  lat?: number;
  lng?: number;
  assignedEmployees: OrderAssignedEmployee[];
  assignedVehicles: OrderAssignedVehicle[];
  assignedMachines: OrderAssignedMachine[];
  requiredMaterials: OrderRequiredMaterial[];
  notes?: string;
  completionNotes?: string;
  actualHours?: number;
  hourlyRate?: number;
  travelCosts?: number;
  originalOfferState?: any;
  // Phase 2: New fields
  contactPerson?: string;
  revenue?: number;
  signatureData?: string;
  signedAt?: string;
  signedBy?: string;
  changeHistory?: ChangeLogEntry[];
  additionalPositions?: OrderAdditionalPosition[];
  frozen?: boolean;
  // Offline-Checkout Felder
  lockedBy?: string;
  lockedByName?: string;
  lockedAt?: string;
  offlineVersion?: number;
  // Wiederkehrende Aufträge (Phase 3b)
  isRecurring?: boolean;
  recurrenceInterval?: 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  recurrenceEndDate?: string;
  parentOrderId?: string;
  // Checklisten (Phase 3c)
  checklist?: { label: string; checked: boolean; checkedBy?: string; checkedAt?: string }[];
  checklistTemplateId?: string;
  // Disposition (Phase 8)
  dispoTasks?: DispoTask[];
  dispoAssigneeId?: string;
  dispoAssigneeName?: string;
  dispoDeadline?: string;
  // Disposition Flow (Phase 10)
  dispoPhase?: 'in_vorbereitung' | 'freigegeben' | null;
  dispoReleasedAt?: string;
  dispoReleasedBy?: string;
}

export interface Notification {
  id: string;
  userId: string;
  type: 'order_assigned' | 'order_returned' | 'offer_accepted' | 'dispo_assigned' | 'info';
  title: string;
  message: string;
  orderId?: string;
  offerId?: string;
  vehicleId?: string;
  machineId?: string;
  inventoryId?: string;
  customerId?: string;
  read: boolean;
  tenantId: string;
  createdAt: string;
}

export interface CustomerContact {
  id: string;
  customerId: string;
  name: string;
  role: string;
  phone: string;
  email: string;
  tenantId: string;
}

export interface CustomerObject {
  id: string;
  customerId: string;
  name: string;
  street: string;
  zip: string;
  city: string;
  lat?: number;
  lng?: number;
  notes: string;
  tenantId: string;
}

export interface EmployeePosition {
  id: string;
  title: string;
  tenantId: string;
}

export interface Employee {
  id: string;
  userId?: string;
  name: string;
  address?: string;
  lat?: number;
  lng?: number;
  positionId?: string;
  approverId?: string;
  status: string;
  tenantId: string;
}

export interface TenantDatevConfig {
  id?: string;
  tenantId: string;
  beraternummer: string;
  mandantennummer: string;
  erloeskonto_19: string;
  erloeskonto_7: string;
  erloeskonto_0: string;
  sammelkonto_debitoren: string;
}

interface DataContextType {
  customers: Customer[];
  offers: Offer[];
  machines: Machine[];
  invoices: Invoice[];
  inventory: InventoryItem[];
  servicePackages: ServicePackage[];
  suppliers: Supplier[];
  vehicles: Vehicle[];
  orders: Order[];
  notifications: Notification[];
  customerContacts: CustomerContact[];
  customerObjects: CustomerObject[];
  employees: Employee[];
  employeePositions: EmployeePosition[];
  tenantDatevConfig: TenantDatevConfig | null;
  addCustomer: (customer: Customer) => void;
  updateCustomer: (id: string, customer: Partial<Customer>) => void;
  addCustomerNote: (customerId: string, note: CustomerHistory) => void;
  deleteCustomerNote: (noteId: number, customerId: string) => void;
  addSupplier: (supplier: Supplier) => void;
  updateSupplier: (id: string, data: Partial<Supplier>) => void;
  deleteSupplier: (id: string) => void;
  addInventorySupplier: (inventoryId: string, supplier: InventorySupplier) => void;
  removeInventorySupplier: (inventoryId: string, supplierId: string) => void;
  addOffer: (offer: Offer) => void;
  deleteOffer: (offerId: string) => void;
  updateOfferStatus: (id: string, status: Offer['status']) => void;
  updateOffer: (id: string, data: Partial<Offer>) => void;
  addMachine: (machine: Machine) => void;
  updateMachine: (id: string, data: Partial<Machine>) => void;
  deleteMachine: (id: string) => void;
  addInvoice: (invoice: Invoice) => void;
  updateInvoice: (id: string, data: Partial<Invoice>) => void;
  addInventoryItem: (item: InventoryItem) => void;
  updateInventoryQuantity: (id: string, delta: number) => void;
  addVehicle: (vehicle: Vehicle) => void;
  updateVehicle: (id: string, data: Partial<Vehicle>) => void;
  deleteVehicle: (id: string) => void;
  addOrder: (order: Order) => void;
  updateOrder: (id: string, data: Partial<Order>) => void;
  deleteOrder: (id: string) => void;
  checkoutOrder: (orderId: string, employeeId: string, employeeName: string) => Promise<boolean>;
  checkinOrder: (orderId: string, changes: Partial<Order>, forceOverwrite?: boolean) => Promise<{ success: boolean; conflict: boolean; error?: string }>;
  adminReleaseOrderLock: (orderId: string) => Promise<boolean>;
  getResourceConflicts: (resourceType: 'machine' | 'vehicle', resourceId: string, startDate: string, endDate: string, excludeOrderId?: string) => Order[];
  addNotification: (notif: Omit<Notification, 'id' | 'createdAt'>) => void;
  markNotificationRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  addCustomerContact: (contact: CustomerContact) => void;
  deleteCustomerContact: (id: string) => void;
  addCustomerObject: (obj: CustomerObject) => void;
  deleteCustomerObject: (id: string) => void;
  addEmployee: (emp: Employee) => void;
  updateEmployee: (id: string, data: Partial<Employee>) => void;
  deleteEmployee: (id: string) => void;
  addEmployeePosition: (pos: EmployeePosition) => void;
  updateEmployeePosition: (id: string, title: string) => void;
  deleteEmployeePosition: (id: string) => void;
  addServicePackage: (pkg: ServicePackage) => void;
  updateServicePackage: (id: string, updates: Partial<ServicePackage>) => void;
  deleteServicePackage: (id: string) => void;
  updateTenantDatevConfig: (config: Partial<TenantDatevConfig>) => void;
  leistungspositionen: LeistungsPosition[];
  leistungsnachweisVerlauf: LeistungsnachweisVersion[];
  loadLeistungsnachweis: (orderId?: string, offerId?: string) => Promise<void>;
  saveLeistungsnachweis: (
    positionen: LeistungsPosition[],
    phase: LeistungsPhase,
    begruendung: string,
    orderId?: string,
    offerId?: string
  ) => Promise<void>;
  updateLeistungsPositionStatus: (positionId: string, status: LeistungsPositionStatus, bemerkung?: string) => Promise<void>;
  isLoading: boolean;
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncedAt: Date | null;
  runSync: () => Promise<void>;
  refreshPendingCount: () => Promise<void>;
  refreshData: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentTenant, currentUser } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [servicePackages, setServicePackages] = useState<ServicePackage[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [customerContacts, setCustomerContacts] = useState<CustomerContact[]>([]);
  const [customerObjects, setCustomerObjects] = useState<CustomerObject[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeePositions, setEmployeePositions] = useState<EmployeePosition[]>([]);
  const [tenantDatevConfig, setTenantDatevConfig] = useState<TenantDatevConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [leistungspositionen, setLeistungspositionen] = useState<LeistungsPosition[]>([]);
  const [leistungsnachweisVerlauf, setLeistungsnachweisVerlauf] = useState<LeistungsnachweisVersion[]>([]);

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const { isOnline, pendingCount, isSyncing, lastSyncedAt, runSync, refreshPendingCount } = useOfflineSync((count) => {
    alert(`✅ ${count} Änderung(en) erfolgreich synchronisiert.`);
    refreshData();
  });
  
  useEffect(() => {
    const loadData = async () => {
      if (!currentTenant) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      if (currentUser) {
        initAuditContext(currentTenant.id, currentUser.id, currentUser.name);
      }

      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 2500));

      const realLoad = async () => {
        try {
          const [
            custRes, histRes, offRes, machRes, invRes, inventRes,
            vehRes, ordRes, notifRes, ccRes, coRes, datevRes,
            suppRes, invSuppRes, empRes, posRes, spRes
          ] = await Promise.allSettled([
            supabase.from('customers').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('customer_history').select('*'),
            supabase.from('offers').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('machines').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('invoices').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('inventory').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('vehicles').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('orders').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('notifications').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('customer_contacts').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('customer_objects').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('tenant_datev_config').select('*').eq('tenant_id', currentTenant.id).maybeSingle(),
            supabase.from('suppliers').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('inventory_suppliers').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('employees').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('employee_positions').select('*').eq('tenant_id', currentTenant.id),
            supabase.from('service_packages').select('*').eq('tenant_id', currentTenant.id)
          ]);

          const getData = (res: PromiseSettledResult<any>) => (res.status === 'fulfilled' ? res.value?.data : null);

          const custData = getData(custRes);
          const histData = getData(histRes);
          const offData = getData(offRes);
          const machData = getData(machRes);
          const invData = getData(invRes);
          const inventData = getData(inventRes);
          const vehData = getData(vehRes);
          const ordData = getData(ordRes);
          const notifData = getData(notifRes);
          const ccData = getData(ccRes);
          const coData = getData(coRes);
          const datevData = getData(datevRes);
          const suppData = getData(suppRes);
          const invSuppData = getData(invSuppRes);
          const empData = getData(empRes);
          const posData = getData(posRes);
          const spData = getData(spRes);

          if (Array.isArray(custData)) {
            const transformedCustomers = custData.map(c => ({
              id: c.id || `c-err-${Math.random()}`,
              name: c.name || 'Unbekannter Kunde',
              contact: c.contact || '',
              phone: c.phone || '',
              email: c.email || '',
              address: c.address || '',
              street: c.street || '',
              zip: c.zip || '',
              city: c.city || '',
              lat: c.lat ? Number(c.lat) : undefined,
              lng: c.lng ? Number(c.lng) : undefined,
              debitorNumber: c.debitor_number || '',
              history: (Array.isArray(histData) ? histData : [])
                .filter(h => h.customer_id === c.id)
                .map(h => ({
                  date: h.date || '',
                  type: h.type || 'Notiz',
                  note: h.note || ''
                }))
            }));
            setCustomers(transformedCustomers);
          }

          if (Array.isArray(offData)) {
            setOffers(offData.map(o => ({
              id: o.id || `o-err-${Math.random()}`,
              customerId: o.customer_id || '',
              date: o.date || '',
              total: Number(o.total) || 0,
              status: (o.status || 'Warten auf Rückmeldung') as any,
              serviceAddress: o.service_address || undefined,
              lat: o.lat ? Number(o.lat) : undefined,
              lng: o.lng ? Number(o.lng) : undefined,
              referenceOfferId: o.reference_offer_id || undefined,
              materials: Array.isArray(o.materials) ? o.materials : [],
              hours: Number(o.hours) || 0,
              rate: Number(o.rate) || 0,
              travelCost: Number(o.travel_cost) || 0,
              internalNotes: o.internal_notes || undefined,
              changeHistory: Array.isArray(o.change_history) ? o.change_history : [],
              token: o.token || undefined,
              signature: o.signature || undefined,
              isAccepted: o.is_accepted || false
            })));
          }

          if (Array.isArray(machData)) {
            setMachines(machData.map(m => ({
              id: m.id || `m-err-${Math.random()}`,
              name: m.name || 'Unbekannte Maschine',
              type: m.type || '',
              status: (m.status || 'Verfügbar') as any,
              location: m.location || '',
              assignee: m.assignee || '',
              nextMaintenance: m.next_maintenance || '',
              supplierId: m.supplier_id || undefined
            })));
          }

          if (Array.isArray(invData)) {
            setInvoices(invData.map(i => ({
              id: i.id || `i-err-${Math.random()}`,
              customerId: i.customer_id || '',
              date: i.date || '',
              amount: Number(i.amount) || 0,
              status: (i.status || 'Offen') as any,
              dueDate: i.due_date || '',
              fromOfferId: i.from_offer_id || '',
              serviceAddress: i.service_address || undefined,
              dunningLevel: i.dunning_level || 0,
              lastDunningDate: i.last_dunning_date || undefined,
              dunningFee: i.dunning_fee || 0
            })));
          }

          if (Array.isArray(inventData)) {
            setInventory(inventData.map(item => {
              const itemSuppliers = (Array.isArray(invSuppData) ? invSuppData : [])
                .filter((s: any) => s.inventory_id === item.id)
                .map((s: any) => ({
                  supplierId: s.supplier_id,
                  supplierItemNumber: s.supplier_item_number,
                  purchasePrice: Number(s.purchase_price) || 0,
                  minOrderQuantity: Number(s.min_order_quantity) || 1
                }));
              return {
                id: item.id || `i-err-${Math.random()}`,
                name: item.name || '',
                category: item.category || '',
                quantity: Number(item.quantity) || 0,
                unit: item.unit || 'Stk',
                minStock: Number(item.min_stock) || 0,
                unitPrice: Number(item.unit_price) || 0,
                suppliers: itemSuppliers
              };
            }));
          }

          if (Array.isArray(vehData)) {
            setVehicles(vehData.map(v => ({
              id: v.id,
              name: v.name || '',
              licensePlate: v.license_plate || '',
              type: v.type || '',
              status: (v.status || 'Verfügbar') as Vehicle['status'],
              assignedEmployeeId: v.assigned_employee_id || undefined,
              nextTuev: v.next_tuev || undefined,
              notes: v.notes || undefined,
              tenantId: v.tenant_id,
              supplierId: v.supplier_id || undefined
            })));
          }

          if (Array.isArray(ordData)) {
            setOrders(ordData.map(o => ({
              id: o.id,
              offerId: o.offer_id || undefined,
              customerId: o.customer_id || '',
              tenantId: o.tenant_id,
              title: o.title || '',
              status: (o.status || 'Bereit') as Order['status'],
              scheduledStart: o.scheduled_start || '',
              scheduledEnd: o.scheduled_end || '',
              serviceAddress: o.service_address || undefined,
              lat: o.lat ? Number(o.lat) : undefined,
              lng: o.lng ? Number(o.lng) : undefined,
              assignedEmployees: Array.isArray(o.assigned_employees) ? o.assigned_employees : [],
              assignedVehicles: Array.isArray(o.assigned_vehicles) ? o.assigned_vehicles : [],
              assignedMachines: Array.isArray(o.assigned_machines) ? o.assigned_machines : [],
              requiredMaterials: Array.isArray(o.required_materials) ? o.required_materials : [],
              notes: o.notes || undefined,
              completionNotes: o.completion_notes || undefined,
              actualHours: o.actual_hours ? Number(o.actual_hours) : undefined,
              hourlyRate: o.hourly_rate ? Number(o.hourly_rate) : undefined,
              travelCosts: o.travel_costs ? Number(o.travel_costs) : undefined,
              originalOfferState: o.original_offer_state || undefined,
              contactPerson: o.contact_person || undefined,
              revenue: o.revenue ? Number(o.revenue) : undefined,
              signatureData: o.signature_data || undefined,
              signedAt: o.signed_at || undefined,
              signedBy: o.signed_by || undefined,
              changeHistory: Array.isArray(o.change_history) ? o.change_history : [],
              additionalPositions: Array.isArray(o.additional_positions) ? o.additional_positions : [],
              frozen: o.frozen || false,
              lockedBy: o.locked_by || undefined,
              lockedByName: o.locked_by_name || undefined,
              lockedAt: o.locked_at || undefined,
              offlineVersion: o.offline_version || 0,
              isRecurring: o.is_recurring || false,
              recurrenceInterval: o.recurrence_interval || undefined,
              recurrenceEndDate: o.recurrence_end_date || undefined,
              parentOrderId: o.parent_order_id || undefined,
              checklist: Array.isArray(o.checklist) ? o.checklist : undefined,
              checklistTemplateId: o.checklist_template_id || undefined,
              attachments: Array.isArray(o.attachments) ? o.attachments : [],
              dispoTasks: Array.isArray(o.dispo_tasks) ? o.dispo_tasks : []
            })));
          }

          if (Array.isArray(notifData)) {
            setNotifications(notifData.map(n => ({
              id: n.id,
              tenantId: n.tenant_id,
              userId: n.user_id || undefined,
              title: n.title,
              message: n.message,
              type: n.type || 'info',
              read: n.read || false,
              createdAt: n.created_at || new Date().toISOString(),
              link: n.link || undefined
            })));
          }

          if (Array.isArray(suppData)) {
            setSuppliers(suppData.map(s => ({
              id: s.id,
              name: s.name || '',
              contact: s.contact || '',
              phone: s.phone || '',
              email: s.email || '',
              street: s.street || '',
              zip: s.zip || '',
              city: s.city || '',
              creditorNumber: s.creditor_number || '',
              notes: s.notes || ''
            })));
          }

          if (Array.isArray(ccData)) {
            setCustomerContacts(ccData.map(c => ({
              id: c.id,
              customerId: c.customer_id || '',
              name: c.name || '',
              role: c.role || '',
              phone: c.phone || '',
              email: c.email || '',
              tenantId: c.tenant_id
            })));
          }

          if (Array.isArray(coData)) {
            setCustomerObjects(coData.map(o => ({
              id: o.id,
              customerId: o.customer_id || '',
              name: o.name || '',
              street: o.street || '',
              zip: o.zip || '',
              city: o.city || '',
              lat: o.lat ? Number(o.lat) : undefined,
              lng: o.lng ? Number(o.lng) : undefined,
              notes: o.notes || '',
              tenantId: o.tenant_id
            })));
          }

          if (Array.isArray(empData)) {
            setEmployees(empData.map(e => ({
              id: e.id,
              userId: e.user_id || undefined,
              name: e.name || '',
              address: e.address || '',
              lat: e.lat ? Number(e.lat) : undefined,
              lng: e.lng ? Number(e.lng) : undefined,
              positionId: e.position_id || undefined,
              status: e.status || 'Aktiv',
              tenantId: e.tenant_id
            })));
          }

          if (Array.isArray(posData)) {
            setEmployeePositions(posData.map(p => ({
              id: p.id,
              title: p.title || '',
              tenantId: p.tenant_id
            })));
          }

          if (Array.isArray(spData)) {
            setServicePackages(spData.map(sp => ({
              id: sp.id,
              name: sp.name || '',
              description: sp.description || '',
              basePrice: Number(sp.base_price) || 0,
              items: Array.isArray(sp.items) ? sp.items : [],
              tenantId: sp.tenant_id
            })));
          }

          if (datevData) {
            setTenantDatevConfig({
              id: datevData.id,
              tenantId: datevData.tenant_id,
              beraternummer: datevData.beraternummer,
              mandantennummer: datevData.mandantennummer,
              erloeskonto_19: datevData.erloeskonto_19,
              erloeskonto_7: datevData.erloeskonto_7,
              erloeskonto_0: datevData.erloeskonto_0,
              sammelkonto_debitoren: datevData.sammelkonto_debitoren
            });
          } else {
            setTenantDatevConfig({
              tenantId: currentTenant.id,
              beraternummer: '', mandantennummer: '',
              erloeskonto_19: '8400', erloeskonto_7: '8300', erloeskonto_0: '8100', sammelkonto_debitoren: '10000'
            });
          }

        } catch (error) {
          console.error('DataContext load failed:', error);
        }
      };

      await Promise.race([realLoad(), timeoutPromise]);
      setIsLoading(false);
    };

    loadData();
  }, [currentTenant, refreshTrigger]);

  useEffect(() => {
    if (!currentTenant) return;
    let debounceTimer: NodeJS.Timeout;
    const handleUpdate = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 1000);
    };

    const channel = supabase.channel(`tenant-${currentTenant.id}`)
      .on('broadcast', { event: 'db_update' }, () => handleUpdate())
      .on('postgres_changes', { event: '*', schema: 'public' }, () => handleUpdate())
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [currentTenant]);

  const handleDbError = (error: any, actionName: string) => {
    if (error) {
      console.error(`DB Error during ${actionName}:`, error);
    } else {
      if (currentTenant) {
        supabase.channel(`tenant-${currentTenant.id}`).send({
          type: 'broadcast',
          event: 'db_update',
          payload: { action: actionName, ts: Date.now() }
        }).catch(() => {});
      }
    }
  };

  const addEmployee = async (emp: Employee) => {
    if (!currentTenant) return;
    setEmployees(prev => [...prev, emp]);
    const { error } = await supabase.from('employees').insert({
      id: emp.id,
      tenant_id: currentTenant.id,
      user_id: emp.userId,
      name: emp.name,
      address: emp.address,
      lat: emp.lat,
      lng: emp.lng,
      position_id: emp.positionId,
      status: emp.status
    });
    handleDbError(error, 'Mitarbeiter anlegen');
  };

  const updateEmployee = async (id: string, data: Partial<Employee>) => {
    setEmployees(prev => prev.map(e => e.id === id ? { ...e, ...data } : e));
    const u: any = {};
    if (data.name !== undefined) u.name = data.name;
    if (data.address !== undefined) u.address = data.address;
    if (data.lat !== undefined) u.lat = data.lat;
    if (data.lng !== undefined) u.lng = data.lng;
    if (data.positionId !== undefined) u.position_id = data.positionId;
    if (data.status !== undefined) u.status = data.status;
    if (data.userId !== undefined) u.user_id = data.userId;
    const { error } = await supabase.from('employees').update(u).eq('id', id);
    handleDbError(error, 'Mitarbeiter aktualisieren');
  };

  const deleteEmployee = async (id: string) => {
    setEmployees(prev => prev.filter(e => e.id !== id));
    const { error } = await supabase.from('employees').delete().eq('id', id);
    handleDbError(error, 'Mitarbeiter löschen');
  };

  const addEmployeePosition = async (pos: EmployeePosition) => {
    if (!currentTenant) return;
    setEmployeePositions(prev => [...prev, pos]);
    const { error } = await supabase.from('employee_positions').insert({
      id: pos.id,
      tenant_id: currentTenant.id,
      title: pos.title
    });
    handleDbError(error, 'Position anlegen');
  };

  const updateEmployeePosition = async (id: string, title: string) => {
    setEmployeePositions(prev => prev.map(p => p.id === id ? { ...p, title } : p));
    const { error } = await supabase.from('employee_positions').update({ title }).eq('id', id);
    handleDbError(error, 'Position aktualisieren');
  };

  const deleteEmployeePosition = async (id: string) => {
    if (!currentTenant) return;
    setEmployeePositions(prev => prev.filter(p => p.id !== id));
    const { error } = await supabase.from('employee_positions').delete().eq('id', id).eq('tenant_id', currentTenant.id);
    handleDbError(error, 'Position löschen');
  };

  const addSupplier = async (supplier: Supplier) => {
    if (!currentTenant) return;
    setSuppliers(prev => [...prev, supplier]);
    const { error } = await supabase.from('suppliers').insert({
      id: supplier.id,
      tenant_id: currentTenant.id,
      name: supplier.name,
      contact: supplier.contact,
      phone: supplier.phone,
      email: supplier.email,
      street: supplier.street,
      zip: supplier.zip,
      city: supplier.city,
      creditor_number: supplier.creditorNumber,
      notes: supplier.notes
    });
    handleDbError(error, 'Lieferant anlegen');
  };

  const updateSupplier = async (id: string, data: Partial<Supplier>) => {
    setSuppliers(prev => prev.map(s => s.id === id ? { ...s, ...data } : s));
    const updateData: any = { ...data };
    if (updateData.creditorNumber !== undefined) {
      updateData.creditor_number = updateData.creditorNumber;
      delete updateData.creditorNumber;
    }
    const { error } = await supabase.from('suppliers').update(updateData).eq('id', id);
    handleDbError(error, 'Lieferant aktualisieren');
  };

  const deleteSupplier = async (id: string) => {
    setSuppliers(prev => prev.filter(s => s.id !== id));
    const { error } = await supabase.from('suppliers').delete().eq('id', id);
    handleDbError(error, 'Lieferant löschen');
  };

  const addInventorySupplier = async (inventoryId: string, supplier: InventorySupplier) => {
    if (!currentTenant) return;
    setInventory(prev => prev.map(inv => {
      if (inv.id === inventoryId) {
        return { ...inv, suppliers: [...(inv.suppliers || []), supplier] };
      }
      return inv;
    }));
    const { error } = await supabase.from('inventory_suppliers').insert({
      tenant_id: currentTenant.id,
      inventory_id: inventoryId,
      supplier_id: supplier.supplierId,
      supplier_item_number: supplier.supplierItemNumber,
      purchase_price: supplier.purchasePrice,
      minimum_order_quantity: supplier.minOrderQuantity
    });
    handleDbError(error, 'Lieferant zu Material hinzufügen');
  };

  const removeInventorySupplier = async (inventoryId: string, supplierId: string) => {
    if (!currentTenant) return;
    setInventory(prev => prev.map(inv => {
      if (inv.id === inventoryId) {
        return { ...inv, suppliers: (inv.suppliers || []).filter(s => s.supplierId !== supplierId) };
      }
      return inv;
    }));
    const { error } = await supabase.from('inventory_suppliers').delete()
      .eq('inventory_id', inventoryId).eq('supplier_id', supplierId).eq('tenant_id', currentTenant.id);
    handleDbError(error, 'Lieferant von Material entfernen');
  };

  const addCustomer = async (customer: Customer) => {
    if (!currentTenant) return;
    setCustomers(prev => [...prev, customer]);
    const { error } = await supabase.from('customers').insert({
      id: customer.id,
      name: customer.name,
      contact: customer.contact,
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      street: customer.street,
      zip: customer.zip,
      city: customer.city,
      lat: customer.lat,
      lng: customer.lng,
      debitor_number: customer.debitorNumber,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Kunde anlegen');
    logAudit('customer', customer.id, 'create');
  };

  const updateCustomer = async (id: string, data: Partial<Customer>) => {
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, ...data } : c));
    const updateData: any = { ...data };
    delete updateData.history;
    if (updateData.debitorNumber !== undefined) {
      updateData.debitor_number = updateData.debitorNumber;
      delete updateData.debitorNumber;
    }
    await supabase.from('customers').update(updateData).eq('id', id);
  };

  const addCustomerNote = async (customerId: string, note: CustomerHistory) => {
    setCustomers(prev => prev.map(c => {
      if (c.id === customerId) return { ...c, history: [note, ...c.history] };
      return c;
    }));
    const { data, error } = await supabase.from('customer_history').insert({
      customer_id: customerId,
      date: note.date,
      type: note.type,
      note: note.note
    }).select().single();
    if (data) {
      setCustomers(prev => prev.map(c => {
        if (c.id === customerId) return { ...c, history: [data, ...c.history.filter(h => h.id !== undefined)] };
        return c;
      }));
    }
    handleDbError(error, 'Kundennotiz anlegen');
  };

  const deleteCustomerNote = async (noteId: number, customerId: string) => {
    setCustomers(prev => prev.map(c => {
      if (c.id === customerId) return { ...c, history: c.history.filter(h => h.id !== noteId) };
      return c;
    }));
    const { error } = await supabase.from('customer_history').delete().eq('id', noteId);
    handleDbError(error, 'Kundennotiz löschen');
  };

  const addOffer = async (offer: Offer) => {
    if (!currentTenant) return;
    setOffers(prev => [...prev, offer]);
    const { error } = await supabase.from('offers').insert({
      id: offer.id,
      customer_id: offer.customerId,
      token: offer.token,
      date: offer.date,
      total: offer.total,
      status: offer.status,
      lat: offer.lat,
      lng: offer.lng,
      service_address: offer.serviceAddress,
      reference_offer_id: offer.referenceOfferId,
      materials: offer.materials,
      hours: offer.hours,
      rate: offer.rate,
      travel_cost: offer.travelCost,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Angebot anlegen');
    logAudit('offer', offer.id, 'create');
  };

  const deleteOffer = async (offerId: string) => {
    setOffers(prev => prev.filter(o => o.id !== offerId));
    const { error } = await supabase.from('offers').delete().eq('id', offerId);
    handleDbError(error, 'Angebot löschen');
    logAudit('offer', offerId, 'delete');
  };

  const updateOfferStatus = async (id: string, status: Offer['status']) => {
    if (!currentTenant) return;
    setOffers(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    const { error } = await supabase.from('offers').update({ status }).eq('id', id);
    handleDbError(error, 'Angebotsstatus aktualisieren');
    
    // Notification bei Angebotszusage
    if (status === 'Angenommen' && currentUser) {
      const offer = offers.find(o => o.id === id);
      const customer = customers.find(c => c.id === offer?.customerId);
      addNotification({
        userId: currentUser.id,
        type: 'offer_accepted',
        title: '🎉 Angebot angenommen!',
        message: `Das Angebot ${id} für ${customer?.name || 'Kunde'} (${offer?.total?.toFixed(2) || 0} €) wurde angenommen.`,
        offerId: id,
        tenantId: currentTenant.id,
        read: false
      });
    }
  };
  
  const addMachine = async (machine: Machine) => {
    if (!currentTenant) return;
    setMachines(prev => [...prev, machine]);
    const { error } = await supabase.from('machines').insert({
      id: machine.id,
      name: machine.name,
      type: machine.type,
      status: machine.status,
      location: machine.location,
      assignee: machine.assignee,
      next_maintenance: machine.nextMaintenance,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Maschine anlegen');
  };

  const updateMachine = async (id: string, data: Partial<Machine>) => {
    setMachines(prev => prev.map(m => m.id === id ? { ...m, ...data } : m));
    const updateData: any = { ...data };
    if (data.nextMaintenance) {
      updateData.next_maintenance = data.nextMaintenance;
      delete updateData.nextMaintenance;
    }
    const { error } = await supabase.from('machines').update(updateData).eq('id', id);
    handleDbError(error, 'Maschine aktualisieren');
  };

  const deleteMachine = async (id: string) => {
    setMachines(prev => prev.filter(m => m.id !== id));
    const { error } = await supabase.from('machines').delete().eq('id', id);
    handleDbError(error, 'Maschine löschen');
  };

  const addServicePackage = async (pkg: ServicePackage) => {
    if (!currentTenant) return;
    setServicePackages(prev => [...prev, pkg]);
    const { error } = await supabase.from('service_packages').insert({
      id: pkg.id,
      tenant_id: currentTenant.id,
      name: pkg.name,
      description: pkg.description,
      base_price: pkg.basePrice,
      items: pkg.items
    });
    handleDbError(error, 'Leistungspaket anlegen');
  };

  const updateServicePackage = async (id: string, updates: Partial<ServicePackage>) => {
    setServicePackages(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    const updateData: any = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.basePrice !== undefined) updateData.base_price = updates.basePrice;
    if (updates.items !== undefined) updateData.items = updates.items;
    
    if (Object.keys(updateData).length > 0) {
      const { error } = await supabase.from('service_packages').update(updateData).eq('id', id);
      handleDbError(error, 'Leistungspaket aktualisieren');
    }
  };

  const deleteServicePackage = async (id: string) => {
    setServicePackages(prev => prev.filter(p => p.id !== id));
    const { error } = await supabase.from('service_packages').delete().eq('id', id);
    handleDbError(error, 'Leistungspaket löschen');
  };
  
  const addInvoice = async (invoice: Invoice) => {
    if (!currentTenant) return;
    setInvoices(prev => [...prev, invoice]);
    const { error } = await supabase.from('invoices').insert({
      id: invoice.id,
      customer_id: invoice.customerId,
      date: invoice.date,
      amount: invoice.amount,
      status: invoice.status,
      due_date: invoice.dueDate,
      from_offer_id: invoice.fromOfferId,
      service_address: invoice.serviceAddress,
      tenant_id: currentTenant.id,
      dunning_level: invoice.dunningLevel || 0,
      last_dunning_date: invoice.lastDunningDate,
      dunning_fee: invoice.dunningFee || 0
    });
    handleDbError(error, 'Rechnung anlegen');
    logAudit('invoice', invoice.id, 'create');
  };

  const updateInvoice = async (id: string, data: Partial<Invoice>) => {
    if (!currentTenant) return;
    const updateData: any = {};
    if (data.status !== undefined) updateData.status = data.status;
    if (data.dunningLevel !== undefined) updateData.dunning_level = data.dunningLevel;
    if (data.lastDunningDate !== undefined) updateData.last_dunning_date = data.lastDunningDate;
    if (data.dunningFee !== undefined) updateData.dunning_fee = data.dunningFee;

    const { error } = await supabase.from('invoices').update(updateData).eq('id', id).eq('tenant_id', currentTenant.id);
    if (!error) {
      setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...data } : inv));
    } else {
      console.error("Error updating invoice:", error);
    }
  };

  const addInventoryItem = async (item: InventoryItem) => {
    if (!currentTenant) return;
    setInventory(prev => [...prev, item]);
    const { error } = await supabase.from('inventory').insert({
      id: item.id,
      name: item.name,
      category: item.category,
      quantity: item.quantity,
      unit: item.unit,
      min_stock: item.minStock,
      unit_price: item.unitPrice,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Lagerartikel anlegen');
  };

  const updateInventoryQuantity = async (id: string, delta: number) => {
    let newQty = 0;
    setInventory(prev => prev.map(item => {
      if (item.id === id) {
        newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
    const { error } = await supabase.from('inventory').update({ quantity: newQty }).eq('id', id);
    handleDbError(error, 'Bestand aktualisieren');
  };

  const addVehicle = async (vehicle: Vehicle) => {
    if (!currentTenant) return;
    setVehicles(prev => [...prev, vehicle]);
    const { error } = await supabase.from('vehicles').insert({
      id: vehicle.id, name: vehicle.name, license_plate: vehicle.licensePlate,
      type: vehicle.type, status: vehicle.status,
      assigned_employee_id: vehicle.assignedEmployeeId,
      notes: vehicle.notes,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Fahrzeug anlegen');
  };

  const updateVehicle = async (id: string, data: Partial<Vehicle>) => {
    setVehicles(prev => prev.map(v => v.id === id ? { ...v, ...data } : v));
    const u: any = {};
    if (data.name !== undefined) u.name = data.name;
    if (data.licensePlate !== undefined) u.license_plate = data.licensePlate;
    if (data.type !== undefined) u.type = data.type;
    if (data.status !== undefined) u.status = data.status;
    if (data.assignedEmployeeId !== undefined) u.assigned_employee_id = data.assignedEmployeeId;
    if (data.nextTuev !== undefined) u.next_tuev = data.nextTuev;
    if (data.notes !== undefined) u.notes = data.notes;
    const { error } = await supabase.from('vehicles').update(u).eq('id', id);
    handleDbError(error, 'Fahrzeug aktualisieren');
  };

  const deleteVehicle = async (id: string) => {
    setVehicles(prev => prev.filter(v => v.id !== id));
    const { error } = await supabase.from('vehicles').delete().eq('id', id);
    handleDbError(error, 'Fahrzeug löschen');
  };

  // ─── Kollisionserkennung für Maschinen/Fahrzeuge ────────────────
  const getResourceConflicts = (resourceType: 'machine' | 'vehicle', resourceId: string, startDate: string, endDate: string, excludeOrderId?: string): Order[] => {
    return orders.filter(o => {
      if (o.id === excludeOrderId) return false;
      if (o.status === 'Storniert' || o.status === 'Abgeschlossen') return false;
      if (!o.scheduledStart || !o.scheduledEnd) return false;
      // Zeitliche Überschneidung prüfen
      const overlaps = o.scheduledStart <= endDate && o.scheduledEnd >= startDate;
      if (!overlaps) return false;
      // Ressource prüfen
      if (resourceType === 'machine') return (o.assignedMachines || []).some(m => m.id === resourceId);
      if (resourceType === 'vehicle') return (o.assignedVehicles || []).some(v => v.id === resourceId);
      return false;
    });
  };

  // ─── Auto-DispoTask Generierung aus Angebot ────────────────────
  const generateDispoTasks = (order: Order): DispoTask[] => {
    const tasks: DispoTask[] = [];
    // Materialien aus dem Auftrag prüfen
    (order.requiredMaterials || []).forEach((mat, idx) => {
      if (!mat.name?.trim()) return;
      const invMatch = inventory.find(inv => 
        inv.name.toLowerCase().includes(mat.name.toLowerCase()) ||
        mat.name.toLowerCase().includes(inv.name.toLowerCase())
      );
      tasks.push({
        id: `dispo-mat-${Date.now()}-${idx}`,
        type: 'material',
        label: mat.name,
        referenceId: invMatch?.id,
        requiredQuantity: mat.quantity,
        availableQuantity: invMatch?.quantity ?? 0,
        unit: mat.unit || invMatch?.unit || 'Stk.',
        status: 'offen',
      });
    });
    // Maschinen
    (order.assignedMachines || []).forEach((m, idx) => {
      tasks.push({
        id: `dispo-mach-${Date.now()}-${idx}`,
        type: 'machine',
        label: m.name,
        referenceId: m.id,
        status: 'offen',
      });
    });
    // Fahrzeuge
    (order.assignedVehicles || []).forEach((v, idx) => {
      tasks.push({
        id: `dispo-veh-${Date.now()}-${idx}`,
        type: 'vehicle',
        label: `${v.name}${v.licensePlate ? ` (${v.licensePlate})` : ''}`,
        referenceId: v.id,
        status: 'offen',
      });
    });
    return tasks;
  };

  const addOrder = async (order: Order) => {
    if (!currentTenant) return;
    
    // Auto-generate dispo tasks if order comes from an offer
    if (!order.dispoTasks && order.offerId) {
      order.dispoTasks = generateDispoTasks(order);
      order.dispoDeadline = order.scheduledStart || undefined;
    }
    
    setOrders(prev => [...prev, order]);
    const { error } = await supabase.from('orders').insert({
      id: order.id, offer_id: order.offerId, customer_id: order.customerId,
      tenant_id: currentTenant.id, title: order.title, status: order.status,
      scheduled_start: order.scheduledStart, scheduled_end: order.scheduledEnd,
      service_address: order.serviceAddress, lat: order.lat, lng: order.lng,
      assigned_employees: order.assignedEmployees, assigned_vehicles: order.assignedVehicles,
      assigned_machines: order.assignedMachines, required_materials: order.requiredMaterials,
      notes: order.notes, completion_notes: order.completionNotes, actual_hours: order.actualHours,
      hourly_rate: order.hourlyRate, travel_costs: order.travelCosts, original_offer_state: order.originalOfferState,
      contact_person: order.contactPerson, revenue: order.revenue,
      additional_positions: order.additionalPositions || [],
      change_history: order.changeHistory || [],
      frozen: false,
      is_recurring: order.isRecurring || false,
      recurrence_interval: order.recurrenceInterval || null,
      recurrence_end_date: order.recurrenceEndDate || null,
      parent_order_id: order.parentOrderId || null,
      checklist: order.checklist || null,
      checklist_template_id: order.checklistTemplateId || null,
      dispo_tasks: order.dispoTasks || null,
      dispo_assignee_id: order.dispoAssigneeId || null,
      dispo_assignee_name: order.dispoAssigneeName || null,
      dispo_deadline: order.dispoDeadline || null,
      dispo_phase: order.dispoPhase || null,
      dispo_released_at: order.dispoReleasedAt || null,
      dispo_released_by: order.dispoReleasedBy || null
    });
    handleDbError(error, 'Auftrag anlegen');
    logAudit('order', order.id, 'create');

    if (!error && order.assignedEmployees && order.assignedEmployees.length > 0) {
      order.assignedEmployees.forEach((emp: any) => {
        const targetUserId = emp.userId || emp.id;
        if (targetUserId) {
          addNotification({
            userId: targetUserId,
            type: 'order_assigned',
            title: 'Neuer Auftrag',
            message: `Dir wurde der Auftrag "${order.title || order.id}" zugewiesen.`,
            orderId: order.id,
            tenantId: currentTenant?.id || '',
            read: false
          });
        }
      });
    }
  };

  const updateOrder = async (id: string, data: Partial<Order>) => {
    if (!currentUser) return;
    const existing = orders.find(o => o.id === id);
    if (!existing) return;
    
    if (existing.frozen && data.status === undefined && data.signatureData === undefined) {
      alert('🔒 Dieser Auftrag wurde abgeschlossen und eingefroren und kann nicht mehr bearbeitet werden.');
      return;
    }
    
    let changesLog = '';
    const u: any = {};
    
    if (data.status !== undefined && data.status !== existing.status) { u.status = data.status; changesLog += `Status geändert auf: ${data.status}\n`; }
    if (data.scheduledStart !== undefined && data.scheduledStart !== existing.scheduledStart) { u.scheduled_start = data.scheduledStart; changesLog += `Geplanter Start geändert: ${data.scheduledStart}\n`; }
    if (data.scheduledEnd !== undefined && data.scheduledEnd !== existing.scheduledEnd) { u.scheduled_end = data.scheduledEnd; changesLog += `Geplantes Ende geändert: ${data.scheduledEnd}\n`; }
    
    if (data.assignedEmployees !== undefined) {
      u.assigned_employees = data.assignedEmployees;
      if (JSON.stringify(data.assignedEmployees) !== JSON.stringify(existing.assignedEmployees)) {
        changesLog += `Personaldisposition geändert\n`;
        const oldEmployees = existing.assignedEmployees || [];
        const newEmployees = data.assignedEmployees;
        const newlyAssigned = newEmployees.filter(ne => !oldEmployees.some(oe => oe.id === ne.id));
        newlyAssigned.forEach((emp: any) => {
          const targetUserId = emp.userId || emp.id;
          if (targetUserId) {
            addNotification({
              userId: targetUserId,
              type: 'order_assigned',
              title: 'Auftrag zugewiesen',
              message: `Dir wurde der Auftrag "${existing.title || existing.id}" neu zugewiesen.`,
              orderId: existing.id,
              tenantId: currentTenant?.id || '',
              read: false
            });
          }
        });
      }
    }
    if (data.assignedVehicles !== undefined) u.assigned_vehicles = data.assignedVehicles;
    if (data.assignedMachines !== undefined) u.assigned_machines = data.assignedMachines;
    if (data.notes !== undefined) u.notes = data.notes;
    if (data.completionNotes !== undefined && data.completionNotes !== existing.completionNotes) { u.completion_notes = data.completionNotes; changesLog += `Abschlussnotiz geändert\n`; }
    if (data.isRecurring !== undefined) u.is_recurring = data.isRecurring;
    if (data.recurrenceInterval !== undefined) u.recurrence_interval = data.recurrenceInterval;
    if (data.recurrenceEndDate !== undefined) u.recurrence_end_date = data.recurrenceEndDate;
    if (data.checklist !== undefined) u.checklist = data.checklist;
    if (data.checklistTemplateId !== undefined) u.checklist_template_id = data.checklistTemplateId;
    if (data.dispoTasks !== undefined) {
      u.dispo_tasks = data.dispoTasks;
      // Lagerbestand anpassen wenn Material-Status auf "bereit" wechselt
      const oldTasks: DispoTask[] = existing.dispoTasks || [];
      const newTasks: DispoTask[] = data.dispoTasks;
      newTasks.forEach(nt => {
        if (nt.type !== 'material' || nt.status !== 'bereit') return;
        const oldTask = oldTasks.find(ot => ot.id === nt.id);
        if (oldTask && oldTask.status === 'bereit') return; // schon abgehakt
        // Material vom Lager abbuchen
        if (nt.referenceId && nt.requiredQuantity) {
          const invItem = inventory.find((inv: any) => inv.id === nt.referenceId);
          if (invItem) {
            const newQty = Math.max(0, invItem.quantity - nt.requiredQuantity);
            updateInventoryQuantity(nt.referenceId, -nt.requiredQuantity);
            // Unterdeckung pr\u00fcfen
            if (newQty < invItem.minStock && currentUser) {
              addNotification({
                userId: currentUser.id,
                type: 'info',
                title: '\u26a0\ufe0f Lagerbestand niedrig',
                message: `${invItem.name}: Bestand nach Entnahme nur noch ${newQty} ${invItem.unit}. Mindestbestand: ${invItem.minStock} ${invItem.unit}. Bitte nachbestellen!`,
                inventoryId: nt.referenceId,
                tenantId: currentTenant?.id || '',
                read: false
              });
            }
          }
        }
      });
    }
    if (data.dispoAssigneeId !== undefined) u.dispo_assignee_id = data.dispoAssigneeId;
    if (data.dispoAssigneeName !== undefined) u.dispo_assignee_name = data.dispoAssigneeName;
    if (data.dispoDeadline !== undefined) u.dispo_deadline = data.dispoDeadline;
    if (data.dispoPhase !== undefined) u.dispo_phase = data.dispoPhase;
    if (data.dispoReleasedAt !== undefined) u.dispo_released_at = data.dispoReleasedAt;
    if (data.dispoReleasedBy !== undefined) u.dispo_released_by = data.dispoReleasedBy;
    
    const isFinanceChanged = 
      (data.actualHours !== undefined && data.actualHours !== existing.actualHours) ||
      (data.hourlyRate !== undefined && data.hourlyRate !== existing.hourlyRate) ||
      (data.travelCosts !== undefined && data.travelCosts !== existing.travelCosts) ||
      (data.requiredMaterials !== undefined && JSON.stringify(data.requiredMaterials) !== JSON.stringify(existing.requiredMaterials)) ||
      (data.additionalPositions !== undefined && JSON.stringify(data.additionalPositions) !== JSON.stringify(existing.additionalPositions));

    if (isFinanceChanged) {
        if (data.actualHours !== undefined) u.actual_hours = data.actualHours;
        if (data.hourlyRate !== undefined) u.hourly_rate = data.hourlyRate;
        if (data.travelCosts !== undefined) u.travel_costs = data.travelCosts;
        if (data.requiredMaterials !== undefined) u.required_materials = data.requiredMaterials;
        if (data.additionalPositions !== undefined) u.additional_positions = data.additionalPositions;
        changesLog += `Auftragskalkulation angepasst\n`;
    }
    
    if (data.title !== undefined) u.title = data.title;
    if (data.contactPerson !== undefined) u.contact_person = data.contactPerson;
    if (data.revenue !== undefined) u.revenue = data.revenue;
    if (data.signatureData !== undefined) { u.signature_data = data.signatureData; changesLog += `Leistungsnachweis digital unterschrieben\n`; }
    if (data.signedAt !== undefined) u.signed_at = data.signedAt;
    if (data.signedBy !== undefined) u.signed_by = data.signedBy;
    if (data.frozen !== undefined) { u.frozen = data.frozen; if (data.frozen) changesLog += `Auftrag wurde eingefroren 🔒\n`; }
    if (data.customerId !== undefined) u.customer_id = data.customerId;
    if (data.serviceAddress !== undefined) u.service_address = data.serviceAddress;
    
    let updatedOrder = { ...existing, ...data };
    
    if (changesLog.trim() && data.changeHistory === undefined) {
      const newLogEntry: ChangeLogEntry = { date: new Date().toISOString(), user: currentUser.name, changes: changesLog.trim() };
      const newHistory = [...(existing.changeHistory || []), newLogEntry];
      u.change_history = newHistory;
      updatedOrder.changeHistory = newHistory;
    } else if (data.changeHistory !== undefined) {
      u.change_history = data.changeHistory;
    }
    
    setOrders(prev => prev.map(o => o.id === id ? updatedOrder : o));
    
    if (!navigator.onLine) {
      await addToSyncQueue({ orderId: id, type: 'update', payload: u });
      if (existing.lockedBy) await saveOrderLocally(updatedOrder);
      await refreshPendingCount();
      return;
    }
    
    const { error } = await supabase.from('orders').update(u).eq('id', id);
    handleDbError(error, 'Auftrag aktualisieren');
    logAudit('order', id, 'update', diffObjects(existing, { ...existing, ...data }));
  };

  const deleteOrder = async (id: string) => {
    setOrders(prev => prev.filter(o => o.id !== id));
    const { error } = await supabase.from('orders').delete().eq('id', id);
    handleDbError(error, 'Auftrag löschen');
    logAudit('order', id, 'delete');
  };

  const addNotification = async (notif: Omit<Notification, 'id' | 'createdAt'>) => {
    if (!currentTenant) return;
    const id = `notif-${Date.now().toString(36)}`;
    const full: Notification = { ...notif, id, createdAt: new Date().toISOString() };
    setNotifications(prev => [full, ...prev]);
    await supabase.from('notifications').insert({
      id, user_id: notif.userId, type: notif.type, title: notif.title,
      message: notif.message, order_id: notif.orderId, 
      offer_id: notif.offerId, vehicle_id: notif.vehicleId, machine_id: notif.machineId,
      inventory_id: notif.inventoryId, customer_id: notif.customerId,
      read: false,
      tenant_id: currentTenant.id
    });
  };

  const markNotificationRead = async (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    await supabase.from('notifications').update({ read: true }).eq('id', id);
  };

  const deleteNotification = async (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    await supabase.from('notifications').delete().eq('id', id);
  };

  const addCustomerContact = async (contact: CustomerContact) => {
    if (!currentTenant) return;
    setCustomerContacts(prev => [...prev, contact]);
    const { error } = await supabase.from('customer_contacts').insert({
      id: contact.id, customer_id: contact.customerId, name: contact.name,
      role: contact.role, phone: contact.phone, email: contact.email,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Ansprechpartner anlegen');
  };

  const deleteCustomerContact = async (id: string) => {
    setCustomerContacts(prev => prev.filter(c => c.id !== id));
    await supabase.from('customer_contacts').delete().eq('id', id);
  };

  const addCustomerObject = async (obj: CustomerObject) => {
    if (!currentTenant) return;
    setCustomerObjects(prev => [...prev, obj]);
    const { error } = await supabase.from('customer_objects').insert({
      id: obj.id, customer_id: obj.customerId, name: obj.name,
      street: obj.street, zip: obj.zip, city: obj.city,
      lat: obj.lat, lng: obj.lng, notes: obj.notes,
      tenant_id: currentTenant.id
    });
    handleDbError(error, 'Kundenobjekt anlegen');
  };

  const deleteCustomerObject = async (id: string) => {
    setCustomerObjects(prev => prev.filter(o => o.id !== id));
    await supabase.from('customer_objects').delete().eq('id', id);
  };

  const updateOffer = async (id: string, data: Partial<Offer>) => {
    if (!currentUser) return;
    const currentOffer = offers.find(o => o.id === id);
    if (!currentOffer) return;
    
    const u: any = {};
    if (data.customerId !== undefined) u.customer_id = data.customerId;
    if (data.token !== undefined) u.token = data.token;
    if (data.date !== undefined) u.date = data.date;
    if (data.total !== undefined) u.total = data.total;
    if (data.status !== undefined) u.status = data.status;
    if (data.lat !== undefined) u.lat = data.lat;
    if (data.lng !== undefined) u.lng = data.lng;
    if (data.serviceAddress !== undefined) u.service_address = data.serviceAddress;
    if (data.referenceOfferId !== undefined) u.reference_offer_id = data.referenceOfferId;
    if (data.materials !== undefined) u.materials = data.materials;
    if (data.hours !== undefined) u.hours = data.hours;
    if (data.rate !== undefined) u.rate = data.rate;
    if (data.travelCost !== undefined) u.travel_cost = data.travelCost;
    if (data.internalNotes !== undefined) u.internal_notes = data.internalNotes;
    if (data.isAccepted !== undefined) u.is_accepted = data.isAccepted;
    if (data.signature !== undefined) u.signature = data.signature;

    setOffers(prev => prev.map(o => o.id === id ? { ...o, ...data } : o));
    const { error } = await supabase.from('offers').update(u).eq('id', id);
    handleDbError(error, 'Angebot aktualisieren');
    logAudit('offer', id, 'update');
  };

  const checkoutOrder = async (orderId: string, employeeId: string, employeeName: string): Promise<boolean> => {
    if (!currentTenant) return false;
    const order = orders.find(o => o.id === orderId);
    if (!order) return false;
    if (order.lockedBy && order.lockedBy !== employeeId) return false;

    const lockedAt = new Date().toISOString();
    const { error } = await supabase.from('orders').update({ locked_by: employeeId, locked_by_name: employeeName, locked_at: lockedAt }).eq('id', orderId);
    if (error) { handleDbError(error, 'Auftrag auschecken'); return false; }
    const updatedOrder = { ...order, lockedBy: employeeId, lockedByName: employeeName, lockedAt };
    setOrders(prev => prev.map(o => o.id === orderId ? updatedOrder : o));
    await saveOrderLocally(updatedOrder);
    return true;
  };

  const convertOrderToDbSchema = (data: Partial<Order>): any => {
    const u: any = {};
    if (data.status !== undefined) u.status = data.status;
    if (data.scheduledStart !== undefined) u.scheduled_start = data.scheduledStart || null;
    if (data.scheduledEnd !== undefined) u.scheduled_end = data.scheduledEnd || null;
    if (data.assignedEmployees !== undefined) u.assigned_employees = data.assignedEmployees;
    if (data.assignedVehicles !== undefined) u.assigned_vehicles = data.assignedVehicles;
    if (data.assignedMachines !== undefined) u.assigned_machines = data.assignedMachines;
    if (data.requiredMaterials !== undefined) u.required_materials = data.requiredMaterials;
    if (data.notes !== undefined) u.notes = data.notes;
    if (data.completionNotes !== undefined) u.completion_notes = data.completionNotes;
    if (data.actualHours !== undefined) u.actual_hours = (data.actualHours === null || data.actualHours === undefined || String(data.actualHours).trim() === '') ? 0 : Number(data.actualHours);
    if (data.hourlyRate !== undefined) u.hourly_rate = (data.hourlyRate === null || data.hourlyRate === undefined || String(data.hourlyRate).trim() === '') ? 0 : Number(data.hourlyRate);
    if (data.travelCosts !== undefined) u.travel_costs = (data.travelCosts === null || data.travelCosts === undefined || String(data.travelCosts).trim() === '') ? 0 : Number(data.travelCosts);
    if (data.originalOfferState !== undefined) u.original_offer_state = data.originalOfferState;
    if (data.contactPerson !== undefined) u.contact_person = data.contactPerson;
    if (data.revenue !== undefined) u.revenue = (data.revenue === null || data.revenue === undefined || String(data.revenue).trim() === '') ? 0 : Number(data.revenue);
    if (data.signatureData !== undefined) u.signature_data = data.signatureData;
    if (data.signedAt !== undefined) u.signed_at = data.signedAt || null;
    if (data.signedBy !== undefined) u.signed_by = data.signedBy;
    if (data.changeHistory !== undefined) u.change_history = data.changeHistory;
    if (data.additionalPositions !== undefined) u.additional_positions = data.additionalPositions;
    if (data.frozen !== undefined) u.frozen = data.frozen;
    if (data.lockedBy !== undefined) u.locked_by = data.lockedBy || null;
    if (data.lockedByName !== undefined) u.locked_by_name = data.lockedByName || null;
    if (data.lockedAt !== undefined) u.locked_at = data.lockedAt || null;
    if (data.offlineVersion !== undefined) u.offline_version = data.offlineVersion;
    if (data.isRecurring !== undefined) u.is_recurring = data.isRecurring;
    if (data.recurrenceInterval !== undefined) u.recurrence_interval = data.recurrenceInterval || null;
    if (data.recurrenceEndDate !== undefined) u.recurrence_end_date = data.recurrenceEndDate || null;
    if (data.parentOrderId !== undefined) u.parent_order_id = data.parentOrderId || null;
    if (data.checklist !== undefined) u.checklist = data.checklist;
    if (data.checklistTemplateId !== undefined) u.checklist_template_id = data.checklistTemplateId || null;
    if (data.dispoTasks !== undefined) u.dispo_tasks = data.dispoTasks;
    if (data.dispoAssigneeId !== undefined) u.dispo_assignee_id = data.dispoAssigneeId || null;
    if (data.dispoAssigneeName !== undefined) u.dispo_assignee_name = data.dispoAssigneeName || null;
    if (data.dispoDeadline !== undefined) u.dispo_deadline = data.dispoDeadline || null;
    if (data.dispoPhase !== undefined) u.dispo_phase = data.dispoPhase || null;
    if (data.dispoReleasedAt !== undefined) u.dispo_released_at = data.dispoReleasedAt || null;
    if (data.dispoReleasedBy !== undefined) u.dispo_released_by = data.dispoReleasedBy || null;
    if (data.customerId !== undefined) u.customer_id = data.customerId || null;
    if (data.serviceAddress !== undefined) u.service_address = data.serviceAddress || null;
    if (data.lat !== undefined) u.lat = (data.lat === null || data.lat === undefined || String(data.lat).trim() === '') ? null : Number(data.lat);
    if (data.lng !== undefined) u.lng = (data.lng === null || data.lng === undefined || String(data.lng).trim() === '') ? null : Number(data.lng);
    return u;
  };

  const checkinOrder = async (orderId: string, changes: Partial<Order>, forceOverwrite = false): Promise<{ success: boolean; conflict: boolean; error?: string }> => {
    if (!currentTenant) return { success: false, conflict: false };
    const localOrder = orders.find(o => o.id === orderId);
    const localVersion = localOrder?.offlineVersion ?? 0;
    const dbChanges = convertOrderToDbSchema(changes);
    const payload = { ...dbChanges, locked_by: null, locked_by_name: null, locked_at: null, offline_version: (localVersion + 1) };
    if (!navigator.onLine) {
      const updated = { ...localOrder, ...changes, lockedBy: undefined, lockedByName: undefined, lockedAt: undefined, offlineVersion: localVersion + 1 };
      setOrders(prev => prev.map(o => o.id === orderId ? updated as Order : o));
      await addToSyncQueue({ orderId, type: 'update', payload: payload });
      await clearLocalOrder(orderId);
      await refreshPendingCount();
      return { success: true, conflict: false };
    }
    try {
      const { data: fresh } = await supabase.from('orders').select('locked_by, offline_version').eq('id', orderId).single();
      if (!forceOverwrite && (fresh?.offline_version ?? 0) > localVersion && !fresh?.locked_by) {
          return { success: false, conflict: true };
      }
    } catch (e) {
      console.warn("Failed to check lock status on checkin:", e);
    }
    const { error } = await supabase.from('orders').update(payload).eq('id', orderId);
    if (error) { 
      handleDbError(error, 'Auftrag einchecken'); 
      return { success: false, conflict: false, error: error.message }; 
    }
    const updated = { ...localOrder, ...changes, lockedBy: undefined, lockedByName: undefined, lockedAt: undefined, offlineVersion: localVersion + 1 };
    setOrders(prev => prev.map(o => o.id === orderId ? updated as Order : o));
    await clearLocalOrder(orderId);
    return { success: true, conflict: false };
  };

  const adminReleaseOrderLock = async (orderId: string): Promise<boolean> => {
    if (!currentTenant) return false;
    const { error } = await supabase.from('orders').update({ locked_by: null, locked_by_name: null, locked_at: null }).eq('id', orderId);
    if (error) { handleDbError(error, 'Sperre aufheben'); return false; }
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, lockedBy: undefined, lockedByName: undefined, lockedAt: undefined } : o));
    return true;
  };

  const updateTenantDatevConfig = async (config: Partial<TenantDatevConfig>) => {
    if (!currentTenant) return;
    const newConfig = { ...tenantDatevConfig, ...config, tenant_id: currentTenant.id };
    const { data: existing } = await supabase.from('tenant_datev_config').select('id').eq('tenant_id', currentTenant.id).maybeSingle();
    let res;
    if (existing) res = await supabase.from('tenant_datev_config').update(newConfig).eq('tenant_id', currentTenant.id);
    else res = await supabase.from('tenant_datev_config').insert([newConfig]);
    if (!res.error) setTenantDatevConfig({ ...(tenantDatevConfig as TenantDatevConfig), ...config });
  };

  // ============================================================
  // Leistungsnachweis CRUD
  // ============================================================

  const loadLeistungsnachweis = async (orderId?: string, offerId?: string) => {
    if (!currentTenant) return;
    let posQuery = supabase.from('leistungspositionen').select('*').eq('tenant_id', currentTenant.id);
    let verlaufQuery = supabase.from('leistungsnachweis_verlauf').select('*').eq('tenant_id', currentTenant.id);
    if (orderId) { posQuery = posQuery.eq('order_id', orderId); verlaufQuery = verlaufQuery.eq('order_id', orderId); }
    else if (offerId) { posQuery = posQuery.eq('offer_id', offerId); verlaufQuery = verlaufQuery.eq('offer_id', offerId); }
    const [posRes, verlaufRes] = await Promise.all([posQuery.order('position_nr'), verlaufQuery.order('version_nr')]);
    if (!posRes.error && posRes.data) {
      setLeistungspositionen(posRes.data.map((p: any): LeistungsPosition => ({
        id: p.id, tenantId: p.tenant_id, orderId: p.order_id, offerId: p.offer_id,
        positionNr: p.position_nr, parentId: p.parent_id || undefined, typ: p.typ, bezeichnung: p.bezeichnung,
        beschreibung: p.beschreibung, menge: p.menge,
        sollMenge: p.soll_menge !== null && p.soll_menge !== undefined ? Number(p.soll_menge) : Number(p.menge),
        istMenge: p.ist_menge !== null && p.ist_menge !== undefined ? Number(p.ist_menge) : Number(p.menge),
        einheit: p.einheit,
        einheitspreis: p.einheitspreis, gesamtpreis: p.gesamtpreis || (p.menge * p.einheitspreis),
        status: p.status, bemerkung: p.bemerkung, createdAt: p.created_at,
        createdById: p.created_by_id, createdByName: p.created_by_name
      })));
    }
    if (!verlaufRes.error && verlaufRes.data) {
      setLeistungsnachweisVerlauf(verlaufRes.data.map((v: any): LeistungsnachweisVersion => ({
        id: v.id, tenantId: v.tenant_id, orderId: v.order_id, offerId: v.offer_id,
        versionNr: v.version_nr, phase: v.phase,
        geaendertVonId: v.geaendert_von_id, geaendertVonName: v.geaendert_von_name,
        geaendertAm: v.geaendert_am, begruendung: v.begruendung,
        diffSummary: v.diff_summary, snapshot: v.snapshot || []
      })));
    }
  };

  const saveLeistungsnachweis = async (
    positionen: LeistungsPosition[],
    phase: LeistungsPhase,
    begruendung: string,
    orderId?: string,
    offerId?: string
  ) => {
    if (!currentTenant || !currentUser) return;

    // 1. UUID-Zuordnung für temporäre IDs (neue Positionen und deren Kinder)
    const idMap = new Map<string, string>();
    const generateUUID = () => {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    };

    positionen.forEach(p => {
      if (p.id && !p.id.startsWith('new-')) {
        idMap.set(p.id, p.id);
      } else {
        idMap.set(p.id, generateUUID());
      }
    });

    // 2. Bestehende Positionen löschen (kaskadiert dank schema cascade)
    if (orderId) {
      await supabase.from('leistungspositionen').delete()
        .eq('tenant_id', currentTenant.id).eq('order_id', orderId);
    } else if (offerId) {
      await supabase.from('leistungspositionen').delete()
        .eq('tenant_id', currentTenant.id).eq('offer_id', offerId);
    }

    // 3. Neue Reihen aufbauen mit aufgelösten UUIDs für parent_id
    const rows = positionen.map((p, idx) => {
      const realId = idMap.get(p.id);
      const realParentId = p.parentId ? idMap.get(p.parentId) : null;

      const row: any = {
        id: realId,
        tenant_id: currentTenant.id,
        order_id: orderId || null,
        offer_id: offerId || null,
        position_nr: idx + 1,
        typ: p.typ,
        bezeichnung: p.bezeichnung,
        beschreibung: p.beschreibung || null,
        menge: p.istMenge !== undefined ? p.istMenge : p.menge,
        soll_menge: p.sollMenge !== undefined ? p.sollMenge : p.menge,
        ist_menge: p.istMenge !== undefined ? p.istMenge : p.menge,
        einheit: p.einheit,
        einheitspreis: p.einheitspreis,
        status: p.status,
        bemerkung: p.bemerkung || null,
        created_by_id: currentUser.id,
        created_by_name: currentUser.name,
        parent_id: realParentId || null
      };
      return row;
    });

    // 4. Sortieren, damit Eltern zuerst eingefügt werden (Foreign Key Checks absichern)
    const sortedRows = [...rows].sort((a, b) => {
      if (a.parent_id === null && b.parent_id !== null) return -1;
      if (a.parent_id !== null && b.parent_id === null) return 1;
      return 0;
    });

    if (sortedRows.length > 0) {
      const { error: insertError } = await supabase.from('leistungspositionen').insert(sortedRows);
      if (insertError) { console.error('Fehler beim Speichern der Leistungspositionen:', insertError); return; }
    }

    // 5. Versionseintrag (Snapshot) anlegen
    const lastVersion = leistungsnachweisVerlauf
      .filter(v => orderId ? v.orderId === orderId : v.offerId === offerId)
      .reduce((max, v) => Math.max(max, v.versionNr), 0);

    await supabase.from('leistungsnachweis_verlauf').insert([{
      tenant_id: currentTenant.id,
      order_id: orderId || null,
      offer_id: offerId || null,
      version_nr: lastVersion + 1,
      phase,
      geaendert_von_id: currentUser.id,
      geaendert_von_name: currentUser.name,
      begruendung,
      snapshot: positionen.map(p => ({
        ...p,
        id: idMap.get(p.id) || p.id,
        parentId: p.parentId ? idMap.get(p.parentId) : undefined
      }))
    }]);

    // 6. Hintergrund-Synchronisation für Legacy-Felder in orders und offers (Abwärtskompatibilität)
    if (orderId) {
      const activePositions = positionen.filter(p => p.status !== 'entfallen');
      const actualHours = activePositions
        .filter(p => p.typ === 'arbeit')
        .reduce((sum, p) => sum + (p.istMenge !== undefined ? p.istMenge : p.menge), 0);
      
      const hourlyRate = activePositions
        .filter(p => p.typ === 'arbeit')
        .reduce((avg, p, _, arr) => avg + p.einheitspreis / arr.length, 0) || 65;

      const requiredMaterials = activePositions
        .filter(p => p.typ === 'material')
        .map(p => ({
          name: p.bezeichnung,
          quantity: p.istMenge !== undefined ? p.istMenge : p.menge,
          unit: p.einheit,
          unitPrice: p.einheitspreis
        }));
      
      const travelCosts = activePositions
        .filter(p => (p.typ === 'pauschale' || p.typ === 'sonstiges') && /anfahrt|fahrt|reise/i.test(p.bezeichnung))
        .reduce((sum, p) => sum + ((p.istMenge !== undefined ? p.istMenge : p.menge) * p.einheitspreis), 0);
      
      const totalRevenue = activePositions
        .reduce((sum, p) => sum + ((p.istMenge !== undefined ? p.istMenge : p.menge) * p.einheitspreis), 0);

      // Aktualisiere DB
      await supabase.from('orders')
        .update({
          actual_hours: actualHours,
          hourly_rate: hourlyRate,
          required_materials: requiredMaterials,
          travel_costs: travelCosts,
          revenue: totalRevenue
        })
        .eq('id', orderId);

      // Aktualisiere lokalen React State für orders
      setOrders(prev => prev.map(o => {
        if (o.id !== orderId) return o;
        return {
          ...o,
          actualHours,
          hourlyRate,
          requiredMaterials,
          travelCosts,
          revenue: totalRevenue
        };
      }));
    } else if (offerId) {
      const activePositions = positionen.filter(p => p.status !== 'entfallen');
      const actualHours = activePositions
        .filter(p => p.typ === 'arbeit')
        .reduce((sum, p) => sum + (p.istMenge !== undefined ? p.istMenge : p.menge), 0);
      
      const rate = activePositions
        .filter(p => p.typ === 'arbeit')
        .reduce((avg, p, _, arr) => avg + p.einheitspreis / arr.length, 0) || 65;

      const requiredMaterials = activePositions
        .filter(p => p.typ === 'material')
        .map(p => ({
          name: p.bezeichnung,
          quantity: p.istMenge !== undefined ? p.istMenge : p.menge,
          price: p.einheitspreis
        }));
      
      const travelCost = activePositions
        .filter(p => (p.typ === 'pauschale' || p.typ === 'sonstiges') && /anfahrt|fahrt|reise/i.test(p.bezeichnung))
        .reduce((sum, p) => sum + ((p.istMenge !== undefined ? p.istMenge : p.menge) * p.einheitspreis), 0);
      
      const totalRevenue = activePositions
        .reduce((sum, p) => sum + ((p.istMenge !== undefined ? p.istMenge : p.menge) * p.einheitspreis), 0);

      // Aktualisiere DB
      await supabase.from('offers')
        .update({
          hours: actualHours,
          rate: rate,
          materials: requiredMaterials,
          travel_cost: travelCost,
          total: totalRevenue
        })
        .eq('id', offerId);

      // Aktualisiere lokalen React State für offers
      setOffers(prev => prev.map(o => {
        if (o.id !== offerId) return o;
        return {
          ...o,
          hours: actualHours,
          rate,
          materials: requiredMaterials,
          travelCost,
          total: totalRevenue
        };
      }));
    }

    // 7. Lokalen State für Leistungspositionen aktualisieren
    await loadLeistungsnachweis(orderId, offerId);
  };

  const updateLeistungsPositionStatus = async (
    positionId: string,
    status: LeistungsPositionStatus,
    bemerkung?: string
  ) => {
    const update: any = { status };
    if (bemerkung !== undefined) update.bemerkung = bemerkung;
    const { error } = await supabase.from('leistungspositionen').update(update).eq('id', positionId);
    if (!error) {
      setLeistungspositionen(prev => prev.map(p => p.id === positionId ? { ...p, status, bemerkung: bemerkung ?? p.bemerkung } : p));
    }
  };

  return (
    <DataContext.Provider value={{ 
      customers, offers, machines, invoices, inventory, servicePackages, suppliers, vehicles, orders,
      notifications, customerContacts, customerObjects, employees, employeePositions, tenantDatevConfig,
      addCustomer, updateCustomer, addCustomerNote, deleteCustomerNote,
      addSupplier, updateSupplier, deleteSupplier, addInventorySupplier, removeInventorySupplier,
      addOffer, deleteOffer, updateOfferStatus, updateOffer,
      addMachine, updateMachine, deleteMachine,
      addInvoice, updateInvoice,
      addInventoryItem, updateInventoryQuantity,
      addVehicle, updateVehicle, deleteVehicle,
      addOrder, updateOrder, deleteOrder, checkoutOrder, checkinOrder, adminReleaseOrderLock,
      addNotification, markNotificationRead, deleteNotification, getResourceConflicts,
      addCustomerContact, deleteCustomerContact,
      addCustomerObject, deleteCustomerObject,
      addEmployee, updateEmployee, deleteEmployee,
      addEmployeePosition, updateEmployeePosition, deleteEmployeePosition,
      addServicePackage, updateServicePackage, deleteServicePackage,
      updateTenantDatevConfig,
      leistungspositionen, leistungsnachweisVerlauf,
      loadLeistungsnachweis, saveLeistungsnachweis, updateLeistungsPositionStatus,
      isLoading,
      isOnline,
      pendingCount,
      isSyncing,
      lastSyncedAt,
      runSync,
      refreshPendingCount,
      refreshData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
